// What "the operator clicked a notification" has to mean, in ONE place — shared
// by the top-bar bell and the Notifications inbox so the two can never drift.
//
// Three things must happen, and only the first is obvious:
//   1. the read receipt (so the badge stops counting it),
//   2. the SITE context, if the notification is about a different site than the
//      one currently selected — in cloud mode a site-scoped page (`/v1/*` is
//      rewritten to `/sites/{siteId}/v1/*` by `resolvePath`) would otherwise
//      show site A's rows for a notification about site B: not empty, WRONG,
//   3. the navigation itself — which is the anchor's own `href`, never this
//      hook, so middle-click / ctrl-click / "open in new tab" keep working.
import { useCallback } from 'preact/hooks';
import { useConfigStore } from '@xantory/store';
import { useMarkNotificationRead, useQueryClient, useSites } from '@xantory/api';
import { resourceHref } from './resource-link';

/** The subset of a notification this hook needs — so the bell can pass its own
 *  row shape without reconstructing an `AppNotification`. */
export interface OpenableNotification {
  id: string;
  read_at?: number | null;
  site_id?: string | null;
  resource_type?: string | null;
  resource_id?: string | null;
}

export function useOpenNotification() {
  const qc = useQueryClient();
  const mode = useConfigStore((s) => s.mode);
  const siteId = useConfigStore((s) => s.siteId);
  const setSite = useConfigStore((s) => s.setSite);
  const markRead = useMarkNotificationRead();
  // Same query key as the shell's switcher — react-query dedupes, no extra fetch.
  const { data: sitesData } = useSites(mode === 'cloud');

  /** Where this notification points, or null when its type has no destination. */
  const hrefFor = useCallback(
    (n: OpenableNotification): string | null => resourceHref(n.resource_type, n.resource_id),
    [],
  );

  const open = useCallback(
    (n: OpenableNotification) => {
      if (!n.read_at) markRead.mutate(n.id);

      if (mode !== 'cloud' || !n.site_id || n.site_id === siteId) return;
      // Only switch to a site the user actually has: `useSites` IS the access
      // list, so a site missing from it would 403 on arrival. Leaving the
      // context alone is the lesser evil — the destination shows its own state
      // rather than an error we invented.
      const target = (sitesData?.sites ?? []).find((s) => s.id === n.site_id);
      if (!target) return;
      setSite(target.id, target.name);
      // Site-scoped query keys don't carry the site id (they're rewritten at the
      // path layer), so the cache still holds the PREVIOUS site's rows. Without
      // this the deep-linked page renders them as if they were the new site's.
      qc.invalidateQueries();
    },
    [mode, siteId, setSite, markRead, sitesData, qc],
  );

  return { hrefFor, open };
}
